import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const CalloutBox = makeShortcode("CalloutBox");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`The Python SDK uses nested contexts for managing state that's shared across events. Contexts are the recommended way to manage things like "which user is taking this action" (through `}<inlineCode parentName="p">{`identify_context`}</inlineCode>{`), rather than manually passing user state through your apps stack.`}</p>
    <p>{`When events (including exceptions) are captured in a context, the event uses the user `}<a parentName="p" {...{
        "href": "/docs/getting-started/identify-users"
      }}>{`distinct ID`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/data/sessions"
      }}>{`session ID`}</a>{`, and tags that are (optionally) set in the context. This is useful for adding properties to multiple events during a single user's interaction with your product.`}</p>
    <p>{`You can enter a context using the `}<inlineCode parentName="p">{`with`}</inlineCode>{` statement:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`from posthog import new_context, tag, set_context_session, identify_context

with new_context():
    tag("transaction_id", "abc123")
    tag("some_arbitrary_value", {"tags": "can be dicts"})

    # Sessions are UUIDv7 values and used to track a sequence of events that occur within a single user session
    # See https://posthog.com/docs/data/sessions
    set_context_session(session_id)

    # Setting the context-level distinct ID. See below for more details.
    identify_context(user_id)

    # This event is captured with the distinct ID, session ID, and tags set above
    posthog.capture("order_processed")
`}</code></pre>
    <p>{`Contexts are persisted across function calls. If you enter one and then call a function and capture an event in the called function, it uses the context tags and session ID set in the parent context:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`from posthog import new_context, tag

def some_function():
    # When called from \`outer_function\`, this event is captured with the property some-key="value-4"
    posthog.capture("order_processed")


def outer_function():
    with new_context():
        tag("some-key", "value-4")
        some_function()

`}</code></pre>
    <p>{`Contexts are nested, so tags added to a parent context are inherited by child contexts. If you set the same tag in both a parent and child context, the child context's value overrides the parent's at event capture (but the parent context won't be affected). This nesting also applies to session IDs and distinct IDs.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`from posthog import new_context, tag

with new_context():
    tag("some-key", "value-1")
    tag("some-other-key", "another-value")
    with new_context():
        tag("some-key", "value-2")
        # This event is captured with some-key="value-2" and some-other-key="another-value"
        posthog.capture("order_processed")

    # This event is captured with some-key="value-1" and some-other-key="another-value"
    posthog.capture("order_processed")

`}</code></pre>
    <p>{`You can disable this nesting behavior by passing `}<inlineCode parentName="p">{`fresh=True`}</inlineCode>{` to `}<inlineCode parentName="p">{`new_context`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`from posthog import new_context

with new_context(fresh=True):
    tag("some-key", "value-2")
    # This event only has the property some-key="value-2" from the fresh context
    posthog.capture("order_processed")

`}</code></pre>
    <blockquote>
      <p parentName="blockquote"><strong parentName="p">{`Note:`}</strong>{` Distinct IDs, session IDs, and properties passed directly to calls to `}<inlineCode parentName="p">{`capture`}</inlineCode>{` and related functions override context state in the final event captured.`}</p>
    </blockquote>
    <h3 {...{
      "id": "contexts-and-user-identification"
    }}>{`Contexts and user identification`}</h3>
    <p>{`Contexts can be associated with a distinct ID by calling `}<inlineCode parentName="p">{`posthog.identify_context`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`from posthog import identify_context

identify_context("distinct-id")
`}</code></pre>
    <p>{`Within a context associated with a distinct ID, all events captured are associated with that user. You can override the distinct ID for a specific event by passing a `}<inlineCode parentName="p">{`distinct_id`}</inlineCode>{` argument to `}<inlineCode parentName="p">{`capture`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`from posthog import new_context, identify_context

with new_context():
    identify_context("distinct-id")
    posthog.capture("order_processed") # will be associated with distinct-id
    posthog.capture("order_processed", distinct_id="another-distinct-id") # will be associated with another-distinct-id
`}</code></pre>
    <p>{`It's recommended to pass the currently active distinct ID from the frontend to the backend, using the `}<inlineCode parentName="p">{`X-POSTHOG-DISTINCT-ID`}</inlineCode>{` header. If you're using our Django middleware, this is extracted and associated with the request handler context automatically.`}</p>
    <p>{`You can read more about identifying users in the `}<a parentName="p" {...{
        "href": "/docs/product-analytics/identify"
      }}>{`user identification documentation`}</a>{`. `}</p>
    <h3 {...{
      "id": "contexts-and-sessions"
    }}>{`Contexts and sessions`}</h3>
    <p>{`Contexts can be associated with a session ID by calling `}<inlineCode parentName="p">{`posthog.set_context_session`}</inlineCode>{`. Session IDs must be UUIDv7 strings.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`from posthog import new_context, set_context_session
with new_context():
    set_context_session(request.get_header("X-POSTHOG-SESSION-ID"))
`}</code></pre>
    <CalloutBox icon="IconInfo" title="Using PostHog on your frontend too?" mdxType="CalloutBox">
      <p>{`If you're using the PostHog JavaScript Web SDK on your frontend, it generates a session ID for you and you can retrieve it by calling `}<inlineCode parentName="p">{`posthog.get_session_id()`}</inlineCode>{`. You then need to pass that session ID to your backend by setting the `}<inlineCode parentName="p">{`X-POSTHOG-SESSION-ID`}</inlineCode>{` header on each fetch request. Alternatively, you can enable '__add_tracing_headers' in your config and the header is set automatically.`}</p>
      <p>{`You need to extract the header in your request handler (if you're using our Django middleware integration, this happens automatically).`}</p>
    </CalloutBox>
    <p>{`If you associate a context with a session, you'll be able to do things like:`}</p>
    <ul>
      <li parentName="ul">{`See backend events on the session timeline when viewing session replays`}</li>
      <li parentName="ul">{`View session replays for users that triggered a backend exception in error tracking`}</li>
    </ul>
    <p>{`You can read more about sessions in the `}<a parentName="p" {...{
        "href": "/docs/data/sessions"
      }}>{`session tracking`}</a>{` documentation.`}</p>
    <h3 {...{
      "id": "exception-capture"
    }}>{`Exception capture`}</h3>
    <p>{`By default exceptions raised within a context are captured and available in the `}<a parentName="p" {...{
        "href": "/docs/error-tracking"
      }}>{`error tracking`}</a>{` dashboard. You can override this behavior by passing `}<inlineCode parentName="p">{`capture_exceptions=False`}</inlineCode>{` to `}<inlineCode parentName="p">{`new_context`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`from posthog import new_context, tag

with new_context(capture_exceptions=False):
    tag("transaction_id", "abc123")
    tag("some_arbitrary_value", {"tags": "can be dicts"})

    # This event will be captured with the tags set above
    posthog.capture("order_processed")
    # This exception will not be captured
    raise Exception("Order processing failed")
`}</code></pre>
    <h3 {...{
      "id": "decorating-functions"
    }}>{`Decorating functions`}</h3>
    <p>{`The SDK exposes a function decorator. It takes the same arguments as `}<inlineCode parentName="p">{`new_context`}</inlineCode>{` and provides a handy way to mark a whole function as being in a new context. For example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-python"
      }}>{`from posthog import scoped, identify_context

@scoped(fresh=True)
def process_order(user, order_id):
    identify_context(user.distinct_id)
    posthog.capture("order_processed") # Associated with the user
    raise Exception("Order processing failed") # This exception is also captured and associated with the user
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      